import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';
import { Upload, AlertCircle, CheckCircle2, AlertTriangle, ChevronDown, Package, CreditCard, Award, HelpCircle, RefreshCw, Plus, ArrowRightLeft, Minus, Check, Shield } from 'lucide-react';
import { parseCollectrCSV, isCollectrFormat, getImportSummary, analyzeSync, getSyncSummary, loadSetNameMapping, computeCollectrFingerprint, type ParsedCollectrItem, type ExistingItem, type SyncAnalysisResult } from '@/lib/collectrImportUtils';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { goldGradient } from '@/lib/gradientStyles';
import { WANTLIST_CATEGORY_LABELS } from '@/types/listing';
import { CollectrPreviewTable } from '@/components/collectr-import/CollectrPreviewTable';
import { useEditablePreview } from '@/components/collectr-import/useEditablePreview';
import { UndoLastSyncButton } from '@/components/collectr-import/UndoLastSyncButton';
import type { EditablePreviewItem, PreviewFilter } from '@/components/collectr-import/types';
import { useProfile } from '@/hooks/useProfile';
import { JobProgress } from '@/components/ui/JobProgress';

interface ImportCollectrDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete?: () => void;
}

type SyncStep = 'upload' | 'analysis' | 'review-removed';

export const ImportCollectrDialog = ({
  open,
  onOpenChange,
  onImportComplete
}: ImportCollectrDialogProps) => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState('');
  const [csvText, setCsvText] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  /** Inline phase readout so long imports/syncs never look frozen. */
  const [importPhase, setImportPhase] = useState<{ label: string; done: number; total: number } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [importResult, setImportResult] = useState<{
    success: ParsedCollectrItem[];
    failures: Array<{ line: number; row: string; reason: string }>;
  } | null>(null);
  const [formatError, setFormatError] = useState<string | null>(null);
  
  // Editable preview state
  const [previewItems, setPreviewItems] = useState<EditablePreviewItem[]>([]);
  const [previewFilter, setPreviewFilter] = useState<PreviewFilter>('all');
  
  const { profile } = useProfile();
  const hasPriorSync = !!profile?.last_collectr_sync_at;

  // Sync mode state — initialized from profile so returning importers see the
  // toggle already ON on the first paint (no mid-interaction flip).
  const [isSyncMode, setIsSyncMode] = useState(hasPriorSync);
  const [syncStep, setSyncStep] = useState<SyncStep>('upload');
  const [existingItems, setExistingItems] = useState<ExistingItem[]>([]);
  const [syncAnalysis, setSyncAnalysis] = useState<SyncAnalysisResult | null>(null);
  const [syncOptions, setSyncOptions] = useState({
    addNew: true,
    updateExisting: true,
    reviewRemoved: false,
    logPartialSales: true, // New option for partial quantity sales
  });
  const [removedItemActions, setRemovedItemActions] = useState<Record<string, 'sold' | 'keep' | 'ignore'>>({});
  const [partialSoldActions, setPartialSoldActions] = useState<Record<string, boolean>>({});
  // Last-known row count from previous sync, used to detect partial-CSV uploads
  const [lastSyncRowCount, setLastSyncRowCount] = useState<number | null>(null);
  // Triggered when this CSV looks dangerously smaller than the previous one
  const [partialCsvWarning, setPartialCsvWarning] = useState<{ prevCount: number; thisCount: number } | null>(null);

  // Fetch existing items when dialog opens (used by the sync analysis step)
  useEffect(() => {
    if (open) {
      fetchExistingItems();
    }
  }, [open]);

  // Re-sync toggle default when the dialog opens, in case the profile query
  // resolved after mount. Only touches the initial upload step.
  useEffect(() => {
    if (open && syncStep === 'upload') {
      setIsSyncMode(hasPriorSync);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hasPriorSync]);

  const fetchExistingItems = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Phase B (profile diet): last_collectr_csv_row_count column was dropped.
    // Derive the previous row count from the most recent sync snapshot instead.
    const [{ data, error }, { data: lastSnap }] = await Promise.all([
      supabase
        .from('user_items')
        .select('id, title, category, condition, quantity, set_code, set_name, card_number, grading_company, slab_grade, finish, cost_basis_cents, language, visibility, is_listed, import_source, collectr_market_price_cents, collectr_price_override_cents, collectr_fingerprint')
        .eq('user_id', user.id)
        .is('sold_at', null),
      supabase
        .from('collectr_sync_snapshots')
        .select('csv_row_count')
        .eq('user_id', user.id)
        .is('undone_at', null)
        .order('synced_at', { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    if (!error && data) {
      setExistingItems(data as ExistingItem[]);
    }
    setLastSyncRowCount(lastSnap?.csv_row_count ?? null);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Invalid File', {
        description: 'Please upload a CSV file'
      });
      return;
    }
    
    setCsvFile(file);
    setFormatError(null);
    
    // Read and preview the CSV
    const reader = new FileReader();
    reader.onload = async e => {
      const text = e.target?.result as string;
      
      // Validate format
      if (!isCollectrFormat(text)) {
        setFormatError('This doesn\'t appear to be a Collectr export. Expected columns: Portfolio Name, Product Name, Variance, Grade.');
        setCsvPreview(text.split('\n').slice(0, 5).join('\n'));
        setCsvText(text);
        return;
      }
      
      setCsvText(text);
      setCsvPreview(text.split('\n').slice(0, 10).join('\n'));
    };
    reader.readAsText(file);
  };

  const handleParseAndPreview = async () => {
    if (!csvText.trim()) {
      toast.error('No File', {
        description: 'Please upload a Collectr CSV export.'
      });
      return;
    }
    
    setIsImporting(true);
    
    try {
      const result = parseCollectrCSV(csvText);
      setImportResult(result);
      
      // Convert to editable preview items
      const editableItems: EditablePreviewItem[] = result.success.map((item, idx) => ({
        ...item,
        _id: `item-${idx}-${Date.now()}`,
        _include: true,
        _modified: false,
        _originalGame: item.game || 'other',
      }));
      setPreviewItems(editableItems);
      setPreviewFilter('all');
      
      // Auto-switch to "needs review" filter if there are items with game = 'other'
      const needsReviewCount = editableItems.filter(i => i.game === 'other').length;
      if (needsReviewCount > 0 && needsReviewCount < editableItems.length) {
        setPreviewFilter('needs-review');
      }
      
      if (isSyncMode && existingItems.length > 0) {
        // Load set name mapping for better match accuracy
        await loadSetNameMapping(supabase);

        // Perform sync analysis
        const analysis = analyzeSync(existingItems, result.success);
        setSyncAnalysis(analysis);
        setSyncStep('analysis');

        // Partial-CSV gate: if this export is >50% smaller than the last one AND
        // >50% of Collectr-origin items would be flagged removed, assume the user
        // uploaded a partial export (one binder, wrong file, etc.) and refuse to
        // default everything to "Sold".
        const collectrCount = existingItems.filter((i) => i.import_source === 'collectr').length;
        const removalRatio = collectrCount > 0 ? analysis.removedItems.length / collectrCount : 0;
        const rowDropRatio = lastSyncRowCount && lastSyncRowCount > 0
          ? 1 - result.success.length / lastSyncRowCount
          : 0;
        const looksPartial = removalRatio > 0.5 && rowDropRatio > 0.5;
        if (looksPartial) {
          setPartialCsvWarning({
            prevCount: lastSyncRowCount ?? 0,
            thisCount: result.success.length,
          });
        } else {
          setPartialCsvWarning(null);
        }

        // Default removed-item action: 'sold' on a normal sync, 'keep' when the
        // partial-CSV gate triggered (don't nuke storefront on a bad upload).
        const defaultActions: Record<string, 'sold' | 'keep' | 'ignore'> = {};
        const defaultAction: 'sold' | 'keep' = looksPartial ? 'keep' : 'sold';
        analysis.removedItems.forEach((item) => {
          defaultActions[item.id] = defaultAction;
        });
        setRemovedItemActions(defaultActions);

        if (analysis.removedItems.length > 0) {
          setSyncOptions((prev) => ({ ...prev, reviewRemoved: true }));
        }

        // Pre-set partial sold actions (default to true for items with quantity decrease)
        const defaultPartialSoldActions: Record<string, boolean> = {};
        analysis.updatedItems.forEach((u) => {
          if (u.partialSold && u.partialSold > 0) {
            defaultPartialSoldActions[u.existing.id] = true;
          }
        });
        setPartialSoldActions(defaultPartialSoldActions);
      } else {
        setShowPreview(true);
      }
    } catch (error) {
      console.error('[ImportCollectrDialog] Parse error:', error);
      toast.error('Parse Error', {
        description: 'Failed to parse CSV. Please check the file format.'
      });
    } finally {
      setIsImporting(false);
    }
  };

  // Handle item field change
  const handleItemChange = useCallback((id: string, field: keyof EditablePreviewItem, value: unknown) => {
    setPreviewItems(prev => prev.map(item => {
      if (item._id !== id) return item;
      return {
        ...item,
        [field]: value,
        _modified: true,
      };
    }));
  }, []);

  // Toggle include/exclude
  const handleToggleInclude = useCallback((id: string) => {
    setPreviewItems(prev => prev.map(item => {
      if (item._id !== id) return item;
      return { ...item, _include: !item._include };
    }));
  }, []);

  // Bulk set game for items
  const handleBulkSetGame = useCallback((game: string, targetFilter?: 'other' | 'all') => {
    setPreviewItems(prev => prev.map(item => {
      if (targetFilter === 'other' && item.game !== 'other') return item;
      if (!item._include) return item;
      
      return {
        ...item,
        game,
        _modified: true,
      };
    }));
  }, []);

  const handleConfirmImport = async () => {
    const itemsToImport = previewItems.filter(item => item._include);
    if (itemsToImport.length === 0) {
      toast.error('No Items Selected', {
        description: 'Please select at least one item to import.'
      });
      return;
    }
    
    setIsImporting(true);
    setImportPhase({ label: 'Preparing items', done: 0, total: itemsToImport.length });
    
    try {
      // Get current user
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        toast.error('Sign in required', {
          description: 'Please sign in to import your inventory.'
        });
        return;
      }
      
      // Prepare items for insertion (use the edited values from previewItems)
      const nowIso = new Date().toISOString();
      const insertData = itemsToImport.map(item => ({
        user_id: user.id,
        title: item.title,
        category: item.category,
        condition: item.condition,
        quantity: item.quantity,
        
        // Optional fields - set_name stores full set name from Collectr
        set_name: item.set_name || null,
        card_number: item.card_number || null,
        rarity: item.rarity || null,
        finish: item.finish || null,
        language: item.language || null,
        game: item.game || 'other',
        
        // Slab fields
        slab_grade: item.slab_grade || null,
        grading_company: item.grading_company || null,
        is_premium_grade: item.is_premium_grade || null,
        
        // Edition
        is_first_edition: item.is_first_edition || null,
        
        // Financial
        cost_basis_cents: item.cost_basis_cents || null,
        // Snapshot prices from the Collectr CSV — never touch asking_price_cents
        // directly; surfaced via the "Use Collectr price" action.
        collectr_market_price_cents: item.market_price_cents ?? null,
        collectr_price_override_cents: item.price_override_cents ?? null,
        
        // Dates
        purchase_date: item.purchase_date || null,
        
        // Notes
        notes: item.notes || null,
        
        // Default marketplace settings
        visibility: 'private' as const,
        is_listed: false,
        is_negotiable: false,
        accepts_trades: false,
        ships_anywhere: false,

        // Origin tracking — these items came from a Collectr CSV import
        import_source: 'collectr',
        collectr_synced_at: nowIso,
        collectr_fingerprint: computeCollectrFingerprint(item),
      }));


      // Batch insert
      setImportPhase({ label: `Saving ${insertData.length} items`, done: 0, total: insertData.length });
      const { error: insertError } = await supabase
        .from('user_items')
        .insert(insertData);
      
      if (insertError) {
        throw insertError;
      }
      setImportPhase({ label: 'Finishing up', done: insertData.length, total: insertData.length });

      // Bump profile last-synced timestamp so the Inventory toolbar can show "Last synced X ago"
      await supabase
        .from('profiles')
        .update({ last_collectr_sync_at: nowIso })
        .eq('user_id', user.id);

      // Calculate summary from imported items
      const totalQuantity = itemsToImport.reduce((sum, i) => sum + i.quantity, 0);
      const sealed = itemsToImport.filter(i => i.category === 'sealed').length;
      const singles = itemsToImport.filter(i => i.category === 'singles').length;
      const slabs = itemsToImport.filter(i => i.category === 'slabs').length;
      
      toast.success('Import Complete', {
        description: `Added ${totalQuantity} items: ${sealed} sealed, ${singles} singles, ${slabs} slabs`
      });
      
      // Reset and close
      handleClose();
      onImportComplete?.();
      
    } catch (error) {
      console.error('[ImportCollectrDialog] Import error:', error);
      toast.error('Import Error', {
        description: error instanceof Error ? error.message : 'Failed to import items. Please try again.'
      });
    } finally {
      setIsImporting(false);
      setImportPhase(null);
    }
  };

  const handleConfirmSync = async () => {
    if (!syncAnalysis) return;
    
    setIsImporting(true);
    setImportPhase({ label: 'Starting sync', done: 0, total: 1 });
    
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        toast.error('Sign in required', {
          description: 'Please sign in to sync your inventory.'
        });
        return;
      }

      let addedCount = 0;
      let updatedCount = 0;
      let soldCount = 0;
      const nowIso = new Date().toISOString();

      // Snapshot diff captured for Undo last sync (up to 24h).
      const diffAdded: string[] = [];
      const diffUpdated: Array<{ id: string; before: Record<string, number | null> }> = [];
      const diffSold: string[] = [];
      const diffSoldBefore: Array<{
        id: string;
        quantity: number | null;
        visibility: string | null;
        is_listed: boolean | null;
      }> = [];

      // Add new items
      if (syncOptions.addNew && syncAnalysis.newItems.length > 0) {
        setImportPhase({ label: `Adding ${syncAnalysis.newItems.length} new items`, done: 0, total: syncAnalysis.newItems.length });
        const itemsToInsert = syncAnalysis.newItems.map(item => ({
          user_id: user.id,
          title: item.title,
          category: item.category,
          condition: item.condition,
          quantity: item.quantity,
          set_name: item.set_name || null,
          card_number: item.card_number || null,
          rarity: item.rarity || null,
          finish: item.finish || null,
          language: item.language || null,
          game: item.game || 'other',
          slab_grade: item.slab_grade || null,
          grading_company: item.grading_company || null,
          is_premium_grade: item.is_premium_grade || null,
          is_first_edition: item.is_first_edition || null,
          cost_basis_cents: item.cost_basis_cents || null,
          collectr_market_price_cents: item.market_price_cents ?? null,
          collectr_price_override_cents: item.price_override_cents ?? null,
          purchase_date: item.purchase_date || null,
          notes: item.notes || null,
          visibility: 'private' as const,
          is_listed: false,
          is_negotiable: false,
          accepts_trades: false,
          ships_anywhere: false,
          // Origin tracking
          import_source: 'collectr',
          collectr_synced_at: nowIso,
          collectr_fingerprint: computeCollectrFingerprint(item),
        }));


        const { data: insertedRows, error } = await supabase
          .from('user_items')
          .insert(itemsToInsert)
          .select('id');
        if (error) throw error;
        addedCount = itemsToInsert.length;
        for (const row of insertedRows ?? []) diffAdded.push(row.id);
      }

      // Update existing items + bump synced_at on every matched item (incl. unchanged
      // and updated) so we know it was confirmed present in the latest export.
      if (syncOptions.updateExisting && syncAnalysis.updatedItems.length > 0) {
        setImportPhase({ label: 'Updating existing items', done: 0, total: syncAnalysis.updatedItems.length });
        for (const { existing, imported, priceOnly } of syncAnalysis.updatedItems) {
          // Capture before-state for undo (only fields we mutate).
          diffUpdated.push({
            id: existing.id,
            before: {
              quantity: existing.quantity ?? null,
              cost_basis_cents: existing.cost_basis_cents ?? null,
              collectr_market_price_cents: existing.collectr_market_price_cents ?? null,
              collectr_price_override_cents: existing.collectr_price_override_cents ?? null,
            },
          });
          // Backfill fingerprint on first re-sync after the feature shipped, so
          // future user edits can no longer break re-matching. Once stamped we
          // never overwrite it.
          const fingerprintBackfill = existing.collectr_fingerprint
            ? {}
            : { collectr_fingerprint: computeCollectrFingerprint(imported) };
          const updatePayload = priceOnly
            ? {
                // Listed item: only refresh stashed Collectr prices, never touch
                // asking_price / qty / cost so the live listing stays stable.
                collectr_market_price_cents: imported.market_price_cents ?? null,
                collectr_price_override_cents: imported.price_override_cents ?? null,
                collectr_synced_at: nowIso,
                ...fingerprintBackfill,
              }
            : {
                quantity: imported.quantity,
                cost_basis_cents: imported.cost_basis_cents || null,
                collectr_market_price_cents: imported.market_price_cents ?? null,
                collectr_price_override_cents: imported.price_override_cents ?? null,
                import_source: 'collectr',
                collectr_synced_at: nowIso,
                ...fingerprintBackfill,
              };

          const { error } = await supabase
            .from('user_items')
            .update(updatePayload)
            .eq('id', existing.id);

          if (error) throw error;
          updatedCount++;
          setImportPhase({
            label: 'Updating existing items',
            done: updatedCount,
            total: syncAnalysis.updatedItems.length,
          });
        }
      }

      // Stamp unchanged matched items as freshly synced too (and tag any that weren't
      // already marked as collectr-origin — first sync after upgrade backfills this).
      const unchangedIds = syncAnalysis.unchangedItems.map(i => i.id);
      if (unchangedIds.length > 0) {
        setImportPhase({ label: 'Stamping matched items', done: 0, total: unchangedIds.length });
        await supabase
          .from('user_items')
          .update({ import_source: 'collectr', collectr_synced_at: nowIso })
          .in('id', unchangedIds);

        // Per-row fingerprint backfill for any unchanged item that doesn't yet
        // have one. Strict-key matched means existing field values equal the
        // imported ones on keyed dimensions, so the existing-row key is the
        // correct fingerprint. Skip rows already stamped.
        const toBackfill = syncAnalysis.unchangedItems.filter(i => !i.collectr_fingerprint);
        for (const item of toBackfill) {
          await supabase
            .from('user_items')
            .update({
              collectr_fingerprint: computeCollectrFingerprint({
                title: item.title,
                category: item.category,
                condition: item.condition,
                quantity: item.quantity,
                set_name: item.set_name,
                card_number: item.card_number,
                grading_company: item.grading_company,
                slab_grade: item.slab_grade,
                finish: item.finish,
              } as ParsedCollectrItem),
            })
            .eq('id', item.id);
        }
      }


      // Handle removed items based on user actions. "Sold" mirrors the manual
      // Mark as Sold path (visibility=private, is_listed=false, quantity=0,
      // sold_at) so items actually disappear from Inventory — previously we only
      // stamped sold_at and left qty/visibility untouched, so rows kept showing.
      const soldNowIso = new Date().toISOString();
      const removedTotal = Object.values(removedItemActions).filter(a => a === 'sold').length;
      if (removedTotal > 0) setImportPhase({ label: 'Marking removed items sold', done: 0, total: removedTotal });
      for (const [itemId, action] of Object.entries(removedItemActions)) {
        if (action === 'sold') {
          const before = syncAnalysis.removedItems.find(i => i.id === itemId);
          const { error } = await supabase
            .from('user_items')
            .update({
              sold_at: soldNowIso,
              visibility: 'private',
              is_listed: false,
              quantity: 0,
            })
            .eq('id', itemId);

          if (error) throw error;
          soldCount++;
          diffSold.push(itemId);
          diffSoldBefore.push({
            id: itemId,
            quantity: before?.quantity ?? null,
            visibility: before?.visibility ?? null,
            is_listed: before?.is_listed ?? null,
          });
          setImportPhase({ label: 'Marking removed items sold', done: soldCount, total: removedTotal });
        }
        // 'keep' and 'ignore' don't require any action
      }

      // Bump last-synced timestamp. Row count now lives on the sync snapshot
      // written below; last_collectr_csv_row_count was dropped in Phase B.
      const csvRowCount = syncAnalysis.newItems.length
        + syncAnalysis.updatedItems.length
        + syncAnalysis.unchangedItems.length
        + syncAnalysis.protectedItems.length;
      await supabase
        .from('profiles')
        .update({ last_collectr_sync_at: nowIso })
        .eq('user_id', user.id);

      // Write the undo snapshot (best-effort — never block the sync on this).
      if (diffAdded.length || diffUpdated.length || diffSold.length) {
        const { error: snapErr } = await supabase
          .from('collectr_sync_snapshots')
          .insert({
            user_id: user.id,
            synced_at: nowIso,
            csv_row_count: csvRowCount,
            diff: { added: diffAdded, updated: diffUpdated, sold: diffSold, sold_before: diffSoldBefore } as never,
          });
        if (snapErr) console.warn('[ImportCollectrDialog] snapshot insert failed', snapErr);
      }

      const messages: string[] = [];
      if (addedCount > 0) messages.push(`${addedCount} added`);
      if (updatedCount > 0) messages.push(`${updatedCount} updated`);
      if (soldCount > 0) messages.push(`${soldCount} marked sold`);

      toast.success('Sync Complete', {
        description: (messages.length > 0 ? messages.join(', ') : 'No changes made')
          + ' • Undo available for 24h from the Import dialog.',
      });

      handleClose();
      onImportComplete?.();
      
    } catch (error) {
      console.error('[ImportCollectrDialog] Sync error:', error);
      toast.error('Sync Error', {
        description: error instanceof Error ? error.message : 'Failed to sync. Please try again.'
      });
    } finally {
      setIsImporting(false);
      setImportPhase(null);
    }
  };

  const handleBack = () => {
    if (syncStep === 'review-removed') {
      setSyncStep('analysis');
    } else if (syncStep === 'analysis') {
      setSyncStep('upload');
      setSyncAnalysis(null);
    } else {
      setShowPreview(false);
      setImportResult(null);
      setPreviewItems([]);
      setPreviewFilter('all');
    }
  };

  const handleClose = () => {
    setCsvFile(null);
    setCsvPreview('');
    setCsvText('');
    setShowPreview(false);
    setImportResult(null);
    setPreviewItems([]);
    setPreviewFilter('all');
    setFormatError(null);
    setIsSyncMode(false);
    setSyncStep('upload');
    setSyncAnalysis(null);
    setSyncOptions({ addNew: true, updateExisting: true, reviewRemoved: false, logPartialSales: true });
    setRemovedItemActions({});
    setPartialSoldActions({});
    setPartialCsvWarning(null);
    onOpenChange(false);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'sealed':
        return <Package className="h-4 w-4 text-info" />;
      case 'singles':
        return <CreditCard className="h-4 w-4 text-caution" />;
      case 'slabs':
        return <Award className="h-4 w-4 text-destructive" />;
      default:
        return null;
    }
  };

  const summary = importResult ? getImportSummary(importResult.success) : null;
  const syncSummary = syncAnalysis ? getSyncSummary(syncAnalysis) : null;
  
  // Calculate included items summary
  const includedItems = previewItems.filter(i => i._include);
  const includedSummary = {
    total: includedItems.length,
    quantity: includedItems.reduce((sum, i) => sum + i.quantity, 0),
    sealed: includedItems.filter(i => i.category === 'sealed').length,
    singles: includedItems.filter(i => i.category === 'singles').length,
    slabs: includedItems.filter(i => i.category === 'slabs').length,
    needsReview: includedItems.filter(i => i.game === 'other').length,
  };

  // Render sync analysis step
  const renderSyncAnalysis = () => {
    if (!syncAnalysis || !syncSummary) return null;

    return (
      <div className="flex flex-col gap-4 flex-1 min-h-0">
        {partialCsvWarning && (
          <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/30 rounded-md">
            <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
            <div className="text-xs space-y-1">
              <p className="font-medium text-destructive">Export looks partial</p>
              <p className="text-muted-foreground">
                Last sync had {partialCsvWarning.prevCount}, this has {partialCsvWarning.thisCount}. Missing items default to <span className="text-foreground">Keep</span> — cancel and re-export if that's wrong.
              </p>
            </div>
          </div>
        )}
        {/* Sync Summary Stats */}
        <div className="grid grid-cols-2 gap-2">
          <div className={cn(
            'flex items-center gap-2 p-3 rounded-md border transition-all',
            'bg-success/10 border-success/20'
          )}>
            <Plus className="h-5 w-5 text-success" />
            <div className="text-sm">
              <p className="font-semibold">{syncSummary.newCount}</p>
              <p className="text-muted-foreground text-xs">New</p>
            </div>
          </div>
          <div className={cn(
            'flex items-center gap-2 p-3 rounded-md border transition-all',
            'bg-info/10 border-info/20'
          )}>
            <ArrowRightLeft className="h-5 w-5 text-info" />
            <div className="text-sm">
              <p className="font-semibold">{syncSummary.updatedCount}</p>
              <p className="text-muted-foreground text-xs">Updated</p>
            </div>
          </div>
          <div className={cn(
            'flex items-center gap-2 p-3 rounded-md border transition-all',
            syncSummary.removedCount > 0 ? 'bg-warning/10 border-warning/20' : 'bg-muted/20 border-white/10'
          )}>
            <Minus className="h-5 w-5 text-warning" />
            <div className="text-sm">
              <p className="font-semibold">{syncSummary.removedCount}</p>
              <p className="text-muted-foreground text-xs">Missing</p>
            </div>
          </div>
          <div className={cn(
            'flex items-center gap-2 p-3 rounded-md border transition-all',
            'bg-muted/20 border-white/10'
          )}>
            <Check className="h-5 w-5 text-muted-foreground" />
            <div className="text-sm">
              <p className="font-semibold">{syncSummary.unchangedCount}</p>
              <p className="text-muted-foreground text-xs">Unchanged</p>
            </div>
          </div>
        </div>

        {/* Protected Items Notice */}
        {syncSummary.protectedCount > 0 && (
          <div className="flex items-center gap-2 p-3 bg-league/10 border border-league/20 rounded-md">
            <Shield className="h-4 w-4 text-league flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-league">{syncSummary.protectedCount} listed protected</p>
              <p className="text-xs text-muted-foreground">Skipped from sync.</p>
            </div>
          </div>
        )}

        {/* Partial Quantity Sales Notice */}
        {syncSummary.partialSoldCount > 0 && (
          <div className="flex items-center gap-2 p-3 bg-warning/10 border border-warning/20 rounded-md">
            <Minus className="h-4 w-4 text-warning flex-shrink-0" />
            <div className="text-sm">
              <p className="font-medium text-warning">{syncSummary.partialSoldCount} qty decreases</p>
              <p className="text-xs text-muted-foreground">Possibly partial sales.</p>
            </div>
          </div>
        )}

        {/* Sync Options */}
        <div className="space-y-3 p-3 backdrop-blur-md bg-black/10 dark:bg-black/20 border border-white/[0.08] rounded-md">
          <p className="text-sm font-medium">Apply:</p>
          
          <label className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-success" />
              <span className="text-sm">Add new ({syncSummary.newCount})</span>
            </div>
            <Switch
              checked={syncOptions.addNew}
              onCheckedChange={(checked) => setSyncOptions(prev => ({ ...prev, addNew: checked }))}
              disabled={syncSummary.newCount === 0}
            />
          </label>
          
          <label className="flex items-center justify-between cursor-pointer">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="h-4 w-4 text-info" />
              <span className="text-sm">Update existing ({syncSummary.updatedCount})</span>
            </div>
            <Switch
              checked={syncOptions.updateExisting}
              onCheckedChange={(checked) => setSyncOptions(prev => ({ ...prev, updateExisting: checked }))}
              disabled={syncSummary.updatedCount === 0}
            />
          </label>
          
          {syncSummary.removedCount > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Minus className="h-4 w-4 text-warning" />
                <span className="text-sm">Review missing ({syncSummary.removedCount})</span>
              </div>
              <span className="text-xs text-muted-foreground">Required</span>
            </div>
          )}
        </div>

        {/* Preview of changes */}
        <div className="flex-1 min-h-0 overflow-auto space-y-2 p-2 backdrop-blur-md bg-black/10 dark:bg-black/20 border border-white/[0.08] rounded-md max-h-[200px]">
          {/* New Items */}
          {syncAnalysis.newItems.slice(0, 5).map((item, idx) => (
            <div key={`new-${idx}`} className="flex items-start gap-2 p-2 backdrop-blur-sm bg-black/10 rounded border border-success/20">
              <Plus className="h-4 w-4 text-success mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0 text-xs">
                <p className="font-medium truncate">{item.title}</p>
                <p className="text-muted-foreground">{item.quantity}x • {WANTLIST_CATEGORY_LABELS[item.category] || item.category}</p>
              </div>
            </div>
          ))}
          {syncAnalysis.newItems.length > 5 && (
            <p className="text-xs text-muted-foreground text-center py-1">
              +{syncAnalysis.newItems.length - 5} more
            </p>
          )}
          
          {/* Updated Items */}
          {syncAnalysis.updatedItems.slice(0, 5).map(({ existing, changes }, idx) => (
            <div key={`updated-${idx}`} className="flex items-start gap-2 p-2 backdrop-blur-sm bg-black/10 rounded border border-info/20">
              <ArrowRightLeft className="h-4 w-4 text-info mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0 text-xs">
                <p className="font-medium truncate">{existing.title}</p>
                <p className="text-info">{changes.join(' • ')}</p>
              </div>
            </div>
          ))}
          {syncAnalysis.updatedItems.length > 5 && (
            <p className="text-xs text-muted-foreground text-center py-1">
              +{syncAnalysis.updatedItems.length - 5} more
            </p>
          )}

          {/* Removed Items Preview */}
          {syncAnalysis.removedItems.slice(0, 3).map((item, idx) => (
            <div key={`removed-${idx}`} className="flex items-start gap-2 p-2 backdrop-blur-sm bg-black/10 rounded border border-warning/20">
              <Minus className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0 text-xs">
                <p className="font-medium truncate">{item.title}</p>
                <p className="text-muted-foreground">Missing from export</p>
              </div>
            </div>
          ))}
          {syncAnalysis.removedItems.length > 3 && (
            <p className="text-xs text-muted-foreground text-center py-1">
              +{syncAnalysis.removedItems.length - 3} more
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            onClick={handleBack}
            variant="ghost"
            disabled={isImporting}
            className="backdrop-blur-sm bg-black/20 dark:bg-black/30 border border-white/[0.08] hover:border-white/20 transition-all"
          >
            Back
          </Button>
          {syncSummary.removedCount > 0 ? (
            <Button
              onClick={() => setSyncStep('review-removed')}
              className={cn('flex-1', goldGradient, 'text-black')}
            >
              Review {syncSummary.removedCount} missing →
            </Button>
          ) : (
            <Button
              onClick={handleConfirmSync}
              className={cn('flex-1', goldGradient, 'text-black')}
              disabled={isImporting || (!syncOptions.addNew && !syncOptions.updateExisting)}
            >
              {isImporting ? 'Syncing...' : 'Apply Sync'}
            </Button>
          )}
        </div>
      </div>
    );
  };

  // Render review removed items step
  const renderReviewRemoved = () => {
    if (!syncAnalysis) return null;

    const soldCount = Object.values(removedItemActions).filter(a => a === 'sold').length;
    const keepCount = Object.values(removedItemActions).filter(a => a === 'keep').length;

    return (
      <div className="flex flex-col gap-4 flex-1 min-h-0">
        <div className="space-y-1">
          <p className="text-sm font-medium">Did you sell these?</p>
          <p className="text-xs text-muted-foreground">
            Missing from this export. Defaulted to <span className="text-warning">Sold</span> — switch any you still own to Keep.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const newActions: Record<string, 'sold' | 'keep' | 'ignore'> = {};
              syncAnalysis.removedItems.forEach(item => {
                newActions[item.id] = 'sold';
              });
              setRemovedItemActions(newActions);
            }}
            className="text-xs backdrop-blur-sm bg-warning/10 border border-warning/20 hover:border-warning/40"
          >
            Mark All Sold
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              const newActions: Record<string, 'sold' | 'keep' | 'ignore'> = {};
              syncAnalysis.removedItems.forEach(item => {
                newActions[item.id] = 'ignore';
              });
              setRemovedItemActions(newActions);
            }}
            className="text-xs backdrop-blur-sm bg-black/20 border border-white/[0.08] hover:border-white/20"
          >
            Ignore All
          </Button>
        </div>

        {/* Items List */}
        <div className="flex-1 min-h-0 overflow-auto space-y-2 p-2 backdrop-blur-md bg-black/10 dark:bg-black/20 border border-white/[0.08] rounded-md max-h-[250px]">
          {syncAnalysis.removedItems.map((item) => (
            <div key={item.id} className="flex items-center gap-2 p-2 backdrop-blur-sm bg-black/10 rounded border border-white/10">
              {getCategoryIcon(item.category)}
              <div className="flex-1 min-w-0 text-xs">
                <p className="font-medium truncate">{item.title}</p>
                <p className="text-muted-foreground">{item.quantity}x</p>
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setRemovedItemActions(prev => ({ ...prev, [item.id]: 'sold' }))}
                  className={cn(
                    'px-2 py-1 text-xs rounded transition-all',
                    removedItemActions[item.id] === 'sold'
                      ? 'bg-warning/30 text-warning border border-warning/50'
                      : 'bg-black/20 text-muted-foreground border border-white/10 hover:border-white/20'
                  )}
                >
                  Sold
                </button>
                <button
                  onClick={() => setRemovedItemActions(prev => ({ ...prev, [item.id]: 'keep' }))}
                  className={cn(
                    'px-2 py-1 text-xs rounded transition-all',
                    removedItemActions[item.id] === 'keep'
                      ? 'bg-success/30 text-success border border-success/50'
                      : 'bg-black/20 text-muted-foreground border border-white/10 hover:border-white/20'
                  )}
                >
                  Keep
                </button>
                <button
                  onClick={() => setRemovedItemActions(prev => ({ ...prev, [item.id]: 'ignore' }))}
                  className={cn(
                    'px-2 py-1 text-xs rounded transition-all',
                    removedItemActions[item.id] === 'ignore'
                      ? 'bg-muted/50 text-foreground border border-white/20'
                      : 'bg-black/20 text-muted-foreground border border-white/10 hover:border-white/20'
                  )}
                >
                  Ignore
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="text-xs text-muted-foreground text-center">
          {soldCount} to mark sold • {keepCount} to keep • {Object.values(removedItemActions).filter(a => a === 'ignore').length} ignored
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            onClick={handleBack}
            variant="ghost"
            disabled={isImporting}
            className="backdrop-blur-sm bg-black/20 dark:bg-black/30 border border-white/[0.08] hover:border-white/20 transition-all"
          >
            Back
          </Button>
          <Button
            onClick={handleConfirmSync}
            className={cn('flex-1', goldGradient, 'text-black')}
            disabled={isImporting}
          >
            {isImporting ? 'Syncing...' : 'Apply Sync'}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent variant="fullscreen" className="sm:max-w-3xl flex flex-col max-sm:h-[100dvh] max-sm:justify-center max-sm:py-10 max-sm:gap-6">
        <DialogHeader className="max-sm:space-y-3 max-sm:text-center max-sm:items-center">
          <DialogTitle className="flex items-center gap-2 max-sm:text-2xl max-sm:justify-center">
            {isSyncMode ? (
              <>
                <RefreshCw className="h-5 w-5 text-active-gold" />
                Sync with Collectr
              </>
            ) : (
              'Import from Collectr'
            )}
          </DialogTitle>
          <DialogDescription className="max-sm:text-sm max-sm:leading-relaxed max-sm:px-2">
            {isSyncMode
              ? 'Compare a fresh export and flag changes.'
              : 'Upload a Collectr CSV to bring your collection in.'
            }
          </DialogDescription>
        </DialogHeader>

        {/* Inline progress — imports/syncs can run for a while, so always show
            which phase is running instead of a silent disabled button. */}
        {importPhase && (
          <JobProgress
            label={importPhase.label}
            done={importPhase.done}
            total={importPhase.total}
            isRunning
          />
        )}


        {/* Sync Analysis Step */}
        {isSyncMode && syncStep === 'analysis' && renderSyncAnalysis()}
        
        {/* Review Removed Step */}
        {isSyncMode && syncStep === 'review-removed' && renderReviewRemoved()}

        {/* Upload module: toggle, undo, dropzone */}
        {syncStep === 'upload' && !showPreview && (
          <div className="flex flex-col gap-3">
            {/* Sync Mode Toggle */}
            <label className="flex items-center justify-between gap-3 px-3 py-2.5 bg-white/[0.03] border border-white/[0.06] rounded-xl cursor-pointer">
              <div className="flex items-center gap-2.5 min-w-0">
                <RefreshCw className="h-4 w-4 text-primary shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-tight">Sync mode</p>
                  <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">Update items instead of duplicating</p>
                </div>
              </div>
              <Switch
                checked={isSyncMode}
                onCheckedChange={(checked) => {
                  setIsSyncMode(checked);
                  if (checked) fetchExistingItems();
                }}
              />
            </label>

            {/* Undo last sync (24h window) */}
            <UndoLastSyncButton onUndone={() => { void fetchExistingItems(); onImportComplete?.(); }} />

            {/* Upload Step (for both modes) */}
            <div className="flex flex-col gap-3">
            {/* Existing items count (sync mode) */}
            {isSyncMode && existingItems.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-2 bg-primary/10 rounded-lg border border-primary/20">
                <CheckCircle2 className="h-4 w-4 text-primary shrink-0" />
                <span className="text-xs">
                  {existingItems.length} to compare
                </span>
              </div>
            )}

            {/* File Upload Area */}
            <div
              className={cn(
                "relative overflow-hidden rounded-2xl border border-dashed text-center transition-all cursor-pointer",
                csvFile
                  ? "border-white/40 bg-white/[0.04] px-4 py-5"
                  : "border-white/15 bg-white/[0.02] hover:border-white/40 px-4 py-9"
              )}
            >
              {/* subtle white radial accent */}
              {!csvFile && (
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 opacity-60"
                  style={{
                    background:
                      'radial-gradient(60% 50% at 50% 0%, rgba(255,255,255,0.08), transparent 70%)',
                  }}
                />
              )}
              {csvFile ? (
                <div className="relative space-y-2.5">
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 text-active-gold" />
                    <span className="font-medium truncate max-w-[200px]">{csvFile.name}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    {(csvFile.size / 1024).toFixed(1)} KB
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setCsvFile(null);
                      setCsvText('');
                      setCsvPreview('');
                      setFormatError(null);
                    }}
                    className="h-8 text-xs text-muted-foreground hover:text-foreground"
                  >
                    Clear file
                  </Button>
                </div>
              ) : (
                <div className="relative space-y-2">
                  <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-white/10 border border-white/30">
                    <Upload className="h-4 w-4 text-white" />
                  </div>
                  <p className="text-sm font-medium">Choose Collectr CSV</p>
                  <p className="text-[11px] text-muted-foreground">Tap or drop a file</p>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                </div>
              )}
            </div>

            {/* Format Error */}
            {formatError && (
              <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                <AlertCircle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-destructive">Format not recognized</p>
                  <p className="text-muted-foreground">{formatError}</p>
                </div>
              </div>
            )}

            {/* CSV Preview */}
            {csvPreview && !formatError && (
              <div className="space-y-2 flex-1 min-h-0 flex flex-col">
                <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Preview (first 10 rows)</label>
                <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 overflow-auto flex-1 min-h-0 max-h-[180px]">
                  <pre className="text-xs font-mono whitespace-pre-wrap break-all">
                    {csvPreview}
                  </pre>
                </div>
              </div>
            )}

            {/* How to Export from Collectr */}
            <Collapsible>
              <CollapsibleTrigger className="group flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground">
                <HelpCircle className="h-3.5 w-3.5" />
                How to export from Collectr
                <ChevronDown className="h-3.5 w-3.5 transition-transform opacity-60 group-data-[state=open]:rotate-180" />
              </CollapsibleTrigger>
              <CollapsibleContent className="mt-2">
                <ol className="text-xs space-y-1 bg-white/[0.02] border border-white/[0.06] p-3 rounded-lg text-muted-foreground list-decimal list-inside">
                  <li>Open Collectr</li>
                  <li>Settings → Export Data</li>
                  <li>Upload the emailed file here</li>
                </ol>
              </CollapsibleContent>
            </Collapsible>

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <Button
                onClick={handleClose}
                variant="ghost"
                className="flex-1 h-11 bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.08]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleParseAndPreview}
                className={cn('flex-1 h-11', goldGradient, 'text-black font-semibold')}
                disabled={isImporting || !csvText.trim() || !!formatError}
              >
                {isSyncMode ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    {isImporting ? 'Analyzing…' : 'Analyze sync'}
                  </>
                ) : (
                  <>
                    {isImporting ? 'Parsing…' : 'Preview import'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
        )}

        {/* Editable Import Preview (non-sync mode) */}
        {!isSyncMode && showPreview && (
          <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Needs Review Warning */}
            {includedSummary.needsReview > 0 && (
              <div className="flex items-center gap-2 p-2 bg-warning/10 rounded-md border border-warning/20">
                <AlertTriangle className="h-4 w-4 text-warning" />
                <span className="text-xs">
                  <span className="font-semibold">{includedSummary.needsReview}</span> items need a game assigned
                </span>
              </div>
            )}
            
            {/* Parse Failures */}
            {importResult && importResult.failures.length > 0 && (
              <Collapsible>
                <CollapsibleTrigger className="flex items-center gap-2 p-2 bg-destructive/10 rounded-md border border-destructive/20 w-full text-left">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <span className="text-xs flex-1">
                    <span className="font-semibold">{importResult.failures.length}</span> rows failed
                  </span>
                  <ChevronDown className="h-4 w-4 text-destructive" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-2 space-y-1 max-h-[100px] overflow-auto">
                  {importResult.failures.slice(0, 10).map((item, idx) => (
                    <div key={idx} className="text-xs p-2 bg-destructive/5 rounded border border-destructive/10">
                      <span className="font-mono text-destructive">Line {item.line}:</span> {item.reason}
                    </div>
                  ))}
                  {importResult.failures.length > 10 && (
                    <p className="text-xs text-muted-foreground text-center py-1">
                      +{importResult.failures.length - 10} more
                    </p>
                  )}
                </CollapsibleContent>
              </Collapsible>
            )}
            
            {/* Editable Preview Table */}
            <CollectrPreviewTable
              items={previewItems}
              onItemChange={handleItemChange}
              onToggleInclude={handleToggleInclude}
              onBulkSetGame={handleBulkSetGame}
              filter={previewFilter}
              onFilterChange={setPreviewFilter}
            />

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                onClick={handleBack}
                variant="ghost"
                disabled={isImporting}
                className="backdrop-blur-sm bg-black/20 dark:bg-black/30 border border-white/[0.08] hover:border-[hsl(40,100%,55%)]/50 active:border-[hsl(40,100%,55%)] transition-all"
              >
                Back
              </Button>
              <Button
                onClick={handleConfirmImport}
                className={cn('flex-1', goldGradient, 'text-black')}
                disabled={isImporting || includedSummary.total === 0}
              >
                {isImporting
                  ? 'Importing...'
                  : `Import ${includedSummary.total} Item${includedSummary.total === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
